import axios from "axios";
import { supabase } from "./supabaseClient"; // Importar o cliente Supabase

// Obter API URL do sistema de configuração dinâmica
const getApiUrl = () => {
  return (
    window.ENV?.REACT_APP_API_URL ||
    process.env.REACT_APP_API_URL ||
    "/api"
  );
};

const API_URL = getApiUrl();

const axiosInstance = axios.create({
  baseURL: API_URL,
});

export const expeditionService = {
  async createExpedition(expeditionData) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para criar expedições."
        );
      }

      const response = await axiosInstance.post(
        `/expeditions`,
        expeditionData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao criar expedição"
        );
      }
      throw error;
    }
  },

  async getExpeditions(filters = {}) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar expedições."
        );
      }

      const response = await axiosInstance.get(`/expeditions`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: filters,
      });
      // Endpoint agora pagina no servidor e devolve { data, total, page, page_size }
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao buscar expedições"
        );
      }
      throw error;
    }
  },

  async getExpeditionById(id) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar detalhes da expedição."
        );
      }

      const response = await axiosInstance.get(`/expeditions/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao buscar detalhes da expedição"
        );
      }
      throw error;
    }
  },

  async updateExpedition(id, expeditionData) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para atualizar expedições."
        );
      }

      const response = await axiosInstance.put(
        `/expeditions/${id}`,
        expeditionData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao atualizar expedição"
        );
      }
      throw error;
    }
  },

  async splitExpedition(id, splitData) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para dividir expedições."
        );
      }

      const response = await axiosInstance.post(
        `/expeditions/${id}/split`,
        { split_items: splitData }, // Enviar os dados dentro de um objeto com a chave 'split_items'
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao dividir expedição"
        );
      }
      throw error;
    }
  },

  async splitExpeditionItem(expeditionId, itemId, quantity) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para dividir itens."
        );
      }

      const response = await axiosInstance.post(
        `/expeditions/${expeditionId}/split-item`,
        { item_id: itemId, quantity: quantity },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao dividir item"
        );
      }
      throw error;
    }
  },

  async updateExpeditionItem(expeditionId, itemId, updateData) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para atualizar itens."
        );
      }

      const response = await axiosInstance.put(
        `/expeditions/${expeditionId}/items/${itemId}`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao atualizar item"
        );
      }
      throw error;
    }
  },

  async getAvailableLots(expeditionId, productId) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar lotes."
        );
      }

      const response = await axiosInstance.get(
        `/expeditions/${expeditionId}/available-lots/${productId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao buscar lotes disponíveis"
        );
      }
      throw error;
    }
  },

  async getMonthlyExpeditionTotal() {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar o total de expedição mensal."
        );
      }

      const response = await axiosInstance.get(`/expeditions/total-monthly-expedition`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message ||
            "Erro ao buscar total de expedição mensal"
        );
      }
      throw error;
    }
  },

  // ⚠️ APENAS EM DESENVOLVIMENTO: Deletar expedição
  async deleteExpedition(id) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para deletar expedições."
        );
      }

      const response = await axiosInstance.delete(`/expeditions/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao deletar expedição"
        );
      }
      throw error;
    }
  },
};
